package com.swetdot.swetgram.ui.chat

import androidx.compose.foundation.Image
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.swetdot.swetgram.domain.Message
import com.swetdot.swetgram.domain.MessageKind
import com.swetdot.swetgram.domain.MessagePreview
import com.swetdot.swetgram.domain.MessageState
import com.swetdot.swetgram.domain.User
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.data.media.VoicePlaybackState
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIconButton
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetImagePhase
import com.swetdot.swetgram.ui.design.SwetOnImage
import com.swetdot.swetgram.ui.design.rememberSwetImage
import com.swetdot.swetgram.ui.design.swetAppear
import com.swetdot.swetgram.ui.design.swetSkeletonPulse
import com.swetdot.swetgram.ui.theme.SwetgramCaptionSmall
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.picso_shared_message
import swetgram.shared.generated.resources.picso_open_shared
import swetgram.shared.generated.resources.chat_edited
import swetgram.shared.generated.resources.chat_message_deleted_body
import swetgram.shared.generated.resources.chat_photo
import swetgram.shared.generated.resources.chat_photo_unavailable
import swetgram.shared.generated.resources.chat_preview_body_photo
import swetgram.shared.generated.resources.chat_voice_message
import swetgram.shared.generated.resources.chat_voice_play
import swetgram.shared.generated.resources.chat_voice_preparing
import swetgram.shared.generated.resources.chat_voice_playback_pause
import swetgram.shared.generated.resources.chat_voice_speed
import swetgram.shared.generated.resources.group_member_you

/**
 * The largest share of the ROW one bubble may take.
 *
 * IT WENT BACK UP TO 0.78 WHEN THE TIMESTAMP CAME BACK INSIDE. It had been cut to 0.72 for exactly
 * one reason: while the metadata sat on the canvas beside the bubble it took about 50dp of the row
 * for an own message, and a bubble allowed 78% on top of that left a 14dp gutter on a 360dp screen
 * -- and that gutter is the strongest cue for which side a message is on. The metadata is inside the
 * bubble again (DECISIONS.md §25), so the row spends nothing on it and the old number is right
 * again. Restored rather than left at 0.72, because a cap kept at a value whose reason has gone is a
 * number the next reader cannot argue with.
 *
 * At 0.78 on a 360dp screen -- 328dp of row once the screen margins are off -- the widest bubble is
 * 255dp. An own message leaves 73dp of gutter; an incoming one spends 32dp on the avatar and its gap
 * and leaves 41dp. Both are far past the 24dp at which a glance can tell the sides apart.
 *
 * A FRACTION, NEVER A FIXED dp. A 300dp cap was wider than the text column on a small phone and
 * absurd on a tablet, which is the defect the fraction replaced and is not worth reintroducing.
 */
internal const val BUBBLE_MAX_WIDTH_FRACTION = 0.78f

/** The gap between the avatar and the bubble: §6.1's 8dp step. */
private val MessageRowGap: Dp = Space.one

/**
 * The gap between the last word of a message and the timestamp trailing it on the same line.
 *
 * §6.1's half step rather than its whole one. 8dp inside a bubble reads as a column break -- the
 * time stops belonging to the sentence and starts looking like a second element -- and the two are
 * already separated by being different sizes and different colours.
 */
private val MetaGap: Dp = Space.half

/**
 * Vertical padding on each row, so consecutive messages sit [Space.one] apart.
 *
 * ONE NUMBER FOR EVERY ROW, WHICH IS A CHANGE. Bubbles used to sit a hairline apart inside a run of
 * one sender's messages and 4dp apart between runs, because §12.4's runs were drawn as a single
 * block with a tail corner. The mockup has no runs: the sender's avatar is on EVERY incoming row and
 * every corner is the same, so a tighter gap inside a run would be grouping nothing to anything.
 */
private val MessageRowGapVertical: Dp = Space.half

/**
 * The metadata's colour on a bubble: that bubble's own text, softened.
 *
 * ONE RULE FOR BOTH DIRECTIONS AND BOTH THEMES, derived rather than picked, and this is the whole of
 * where the timestamp's colour comes from now that it is back inside the bubble. It cannot be
 * `ChatColors.meta` any more: that role is `text.secondary`, a grey chosen to sit on the app's dark
 * surfaces and on the black canvas, and on the near-white OWN bubble it measures about 2.3:1 -- the
 * same trap the deleted-message tombstone fell into and was fixed for.
 *
 * SOFTENED TEXT IS ALSO WHAT THE SPEC ASKS FOR. SDS §12.4 writes the in-bubble timestamp as "white
 * with reduced opacity", so an alpha over the bubble's own foreground is the design system's own
 * construction rather than an invention here -- and because the source is the FOREGROUND role, it
 * follows the bubble in every theme without a branch: white over dark grey incoming, near-black over
 * near-white outgoing, and both inverted in the light theme.
 *
 * [META_ON_BUBBLE_ALPHA] is 0.7, and the number has a floor under it rather than being a taste.
 * Composited over the four surfaces this screen actually draws, the dimmest pairing is the LIGHT
 * theme's incoming bubble at about 6.3:1 -- past WCAG AA's 4.5:1 for the normal-size text a 10sp
 * timestamp is. That pairing reaches exactly 4.5:1 at an alpha of about 0.60, so the 0.7 shipped is
 * headroom rather than a coincidence, and softening it further for looks is not a free change.
 * `BubbleMetaContrastTest` measures all four composites and fails if a palette edit takes any of
 * them under AA.
 */
internal fun bubbleMetaColor(onBubble: Color): Color = onBubble.copy(alpha = META_ON_BUBBLE_ALPHA)

/** See [bubbleMetaColor]. Not private: the contrast test measures the composite it produces. */
internal const val META_ON_BUBBLE_ALPHA = 0.7f

/**
 * The three shapes a message can take.
 *
 * The bubble is §12.4's, with the mockup's override of its colours (see `ChatColors` and
 * DECISIONS.md §25). Where the timestamp goes is NOT one of the overrides any more: it is inside the
 * bubble trailing the text, which is what §12.4 says and what the user asked for after seeing the
 * screen on a phone. §12.3's attachment list is the only place the SDS mentions images and it says
 * nothing about how a photo and a bubble combine, so the mockup decides that: a captioned photo is a
 * bubble with the caption ABOVE an inset image.
 *
 * A photo sent with NO caption keeps the treatment that was already here -- no bubble at all -- and
 * the mockup does not contradict it because the mockup contains no uncaptioned photo. The coloured
 * rectangle behind a photograph carries no information its own edges do not, and it stains a
 * transparent PNG.
 *
 * The timestamp requirement survives all three: on both photo surfaces the metadata is laid over the
 * image on a scrim rather than disappearing.
 */
internal enum class MessageSurface {

    /** Text, the deleted tombstone, anything that is not a photo. */
    Bubble,

    /** No bubble. The image itself is the surface, with the metadata laid over its corner. */
    Photo,

    /** A bubble holding the caption, then the image inset inside it with its own radius. */
    CaptionedPhoto,

    Voice,
}

/**
 * Which of the three a message is drawn as.
 *
 * Pure, and separated from the drawing for the same reason [photoBoxAspectRatio] is: it is a
 * decision with more cases than it looks like -- five inputs' worth of combinations -- and this
 * host has no emulator to discover a wrong branch on (CLAUDE.md: no device in the build).
 *
 * Deliberately decided from the message ALONE, never from the load phase. A photo that is still
 * downloading and one that has failed must land on the same surface as one that succeeded,
 * because a surface that changed when the bytes arrived would restructure the row mid-scroll and
 * shove the rest of the timeline under the reader's finger.
 *
 * The rules, in order:
 *  - A deleted message is a tombstone sentence, whatever it used to be. Bubble.
 *  - [MessageKind] is authoritative: a TEXT message that somehow carries a URL is still text.
 *  - `type: image` and a present `media.url` are two separate facts on the wire (see the Message
 *    schema in `docs/api/openapi.yaml`, where `media` is `oneOf` MediaObject or null). With no
 *    URL there will never BE an image to act as the surface or to scrim metadata against, so
 *    this degrades to the ordinary bubble and reports the failure inside it.
 *  - The contract allows `content` on an `image` message -- that is the caption -- and its
 *    presence is what decides between the last two. Blank counts as absent: a bubble drawn for a
 *    caption of `"   "` would be a photo with an empty strip over it.
 */
internal fun messageSurface(
    kind: MessageKind,
    imageUrl: String?,
    caption: String?,
    isDeleted: Boolean,
    hasReply: Boolean = false,
): MessageSurface = when {
    isDeleted -> MessageSurface.Bubble
    kind == MessageKind.IMAGE && imageUrl.isNullOrBlank() -> MessageSurface.Bubble
    kind == MessageKind.IMAGE && (hasReply || !caption.isNullOrBlank()) -> MessageSurface.CaptionedPhoto
    kind == MessageKind.IMAGE -> MessageSurface.Photo
    kind == MessageKind.VOICE -> MessageSurface.Voice
    else -> MessageSurface.Bubble
}

/** [messageSurface] for a whole [Message]; `text` is the caption for an image message. */
internal fun Message.surface(): MessageSurface =
    messageSurface(
        kind = kind,
        imageUrl = imageUrl,
        caption = text,
        isDeleted = isDeleted,
        hasReply = replyTo != null,
    )

/**
 * How wide the bubble may be, given the row it is in and what else is sharing it.
 *
 * PURE, because a measure block cannot be asserted on a host with no renderer (CLAUDE.md: no device,
 * no emulator), and this is the arithmetic every awkward message on this screen lands on.
 *
 * `trailingWidth` IS GONE FROM THIS SIGNATURE, and its absence is the point. It existed for the
 * timestamp that used to sit on the canvas beside the bubble; with the metadata back inside, the row
 * has exactly two children -- the sender's avatar and the bubble -- and every call site was passing
 * zero. A parameter with one possible argument is a parameter that describes a layout the screen no
 * longer has, and the next reader would have had to work out for themselves that nothing trails
 * anything.
 *
 * TWO LIMITS, WHICHEVER IS SMALLER. [fraction] of the row is the LOOK -- it is what leaves a gutter
 * on the far side so a reader can see which way a message points. `room` is the GUARANTEE: the
 * avatar is measured first and the bubble is only ever offered what is left. On a phone the fraction
 * is always the binding one; `room` only bites on a very narrow window, and it degrades by shrinking
 * the bubble rather than by pushing the avatar off the edge.
 *
 * Floored at zero: a window narrower than the avatar would otherwise hand `Constraints` a negative
 * maximum, which throws.
 */
internal fun bubbleMaxWidthPx(
    rowWidth: Int,
    leadingWidth: Int,
    fraction: Float = BUBBLE_MAX_WIDTH_FRACTION,
): Int {
    val room = rowWidth - leadingWidth
    val cap = (rowWidth * fraction).toInt()
    return minOf(room, cap).coerceAtLeast(0)
}

/**
 * Where the trailing metadata ends up, and how big the bubble's content is once it is there.
 *
 * THIS IS THE ARITHMETIC THE MOVE BACK INSIDE COST, and it is the part a screenshot of a happy
 * conversation will never show. While the timestamp was on the canvas the bubble was sized to its
 * text alone and there was nothing to fit; now the last line of the message and the metadata are
 * competing for the same run of pixels, and the cases that decide the layout are the ugly ones:
 *
 *  - A MESSAGE NARROWER THAN ITS OWN METADATA -- "ok" at 18:45 with two checks. The bubble is
 *    widened to the metadata rather than the metadata being squeezed under a two-letter word. This
 *    is why [width] is a `maxOf` and not [textWidth].
 *  - A LAST LINE THAT ENDS NEAR THE CAP. The metadata cannot sit beside it, so it DROPS TO ITS OWN
 *    LINE under the text, right-aligned. Not overlapped, not clipped, not allowed to widen the
 *    bubble past what the row offered -- all three of which are what a `Row` would have done here.
 *  - A SINGLE UNBREAKABLE WORD. A 60-character URL cannot wrap, so the text placeable comes back at
 *    the full cap and [lastLineWidth] can come back LARGER than the cap. The comparison is against
 *    [maxWidth] rather than against the text's own width for exactly that reason, and the result is
 *    clamped, so an overflowing line cannot drag the bubble out of the row with it.
 *  - A 4096-CHARACTER MESSAGE (DECISIONS §5's ceiling). It wraps to the cap and lands in whichever
 *    branch its final line falls into, like any other message: there is no case for length here
 *    because length is not an input.
 *
 * [lastLineWidth] IS [UNKNOWN_LAST_LINE] UNTIL THE TEXT HAS BEEN LAID OUT ONCE. Compose only knows
 * where a line ends after it has measured it, and the answer arrives through `onTextLayout` a frame
 * later. Unknown means BELOW -- the layout that is always safe, never overlaps and never clips --
 * so the worst a first frame can do is be one line too tall, and it settles on the next pass. It
 * cannot oscillate: the text is measured against [maxWidth] whichever branch is taken, so the
 * decision never changes the input it was made from.
 */
internal fun bubbleMetaLayout(
    textWidth: Int,
    textHeight: Int,
    lastLineWidth: Int,
    metaWidth: Int,
    metaHeight: Int,
    gap: Int,
    maxWidth: Int,
): BubbleMetaLayout {
    val cap = maxWidth.coerceAtLeast(0)
    val beside = lastLineWidth != UNKNOWN_LAST_LINE && lastLineWidth + gap + metaWidth <= cap
    return if (beside) {
        BubbleMetaLayout(
            metaBesideText = true,
            width = maxOf(textWidth, lastLineWidth + gap + metaWidth).coerceIn(0, cap),
            // A `maxOf` rather than [textHeight] alone: at 10sp against 16sp the metadata is always
            // the shorter of the two, but a reader at 200% font scale is not obliged to keep it so.
            height = maxOf(textHeight, metaHeight),
        )
    } else {
        BubbleMetaLayout(
            metaBesideText = false,
            width = maxOf(textWidth, metaWidth).coerceIn(0, cap),
            height = textHeight + metaHeight,
        )
    }
}

/** The answer [bubbleMetaLayout] gives: where the metadata goes, and the size that leaves. */
internal data class BubbleMetaLayout(
    /** True: on the last line of the text. False: on a line of its own beneath it. */
    val metaBesideText: Boolean,
    val width: Int,
    val height: Int,
)

/** "The text has not been laid out yet", which [bubbleMetaLayout] treats as "put it below". */
internal const val UNKNOWN_LAST_LINE = -1

/**
 * One message.
 *
 * WHAT LEFT THIS SIGNATURE. `isRunStart` and `isRunEnd` did. They existed to square off one corner
 * at the end of a run of messages from the same sender, which the mockup does not do: every corner
 * is the same radius and every incoming row carries its sender's avatar, so there is no run for the
 * flags to describe. `buildTimeline` still computes them -- they are domain, they are tested, and
 * the chat list's own grouping may want them -- but this file no longer reads them, and taking them
 * as parameters it ignored would have been worse than dropping them.
 *
 * [zone] is threaded in rather than defaulted at the point of use: the day separators above these
 * bubbles are computed from the zone `ChatScreen` was given, and a bubble reading the system zone
 * independently would put a message under the wrong "Today" whenever the two differ -- which is
 * every test that pins a zone, and any future per-account timezone.
 */
@Composable
fun MessageBubble(
    message: Message,
    me: UserId,
    modifier: Modifier = Modifier,
    zone: TimeZone = TimeZone.currentSystemDefault(),
    members: List<User> = emptyList(),
    onReactionClick: (String) -> Unit = {},
    /**
     * Whether the row's avatar names its sender out loud -- see [SenderAvatar].
     *
     * TRUE FOR A GROUP AND FALSE FOR A 1:1, and it defaults to false because the overwhelming case
     * is the direct chat, where the header has already named the peer and repeating it on every row
     * is noise. It has no visual effect in either case: the disc is drawn identically and only its
     * semantics change.
     */
    announceSender: Boolean = false,
    /**
     * SDS §25.1's explanation for a message that did not go -- see `ChatScreen.failedSendNote`.
     *
     * Drawn OUTSIDE the bubble, deliberately, and it stays outside now that the timestamp has moved
     * back in. It is not metadata: it is a sentence ABOUT the message -- why it is still sitting
     * there and what to do -- and putting a wrapped sentence inside the bubble would make it read as
     * something the sender wrote. Its size is unchanged for the same reason: `caption` 12sp, because
     * it is prose to be read rather than a mark to be glanced at.
     */
    failureNote: String? = null,
    voicePlayback: VoicePlaybackState = VoicePlaybackState(),
    onVoicePlaybackToggle: (Message) -> Unit = {},
    onVoicePlaybackSeek: (Message, Long) -> Unit = { _, _ -> },
    onVoicePlaybackSpeed: (Message) -> Unit = {},
    onBubblePositioned: ((LayoutCoordinates) -> Unit)? = null,
) {
    val mine = message.isMine(me)
    val colors = SwetgramThemeTokens.colors
    val surface = message.surface()

    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = Space.two, vertical = MessageRowGapVertical),
        // Own messages right, everyone else's left. The row below does the real placement; this
        // only governs the failure note under it.
        horizontalAlignment = if (mine) Alignment.End else Alignment.Start,
    ) {
        MessageRow(
            mine = mine,
            leading = {
                // THE AVATAR IS ON EVERY INCOMING ROW, WITH NO GROUPING AND NO RESERVED GAP. That is
                // the mockup, and it is why nothing here consults the run flags.
                //
                // ONE URL, N ROWS, ONE FETCH. A 1:1 conversation repeats one avatar down the whole
                // screen and this draws a separate `SwetAvatar` per row, which is only affordable
                // because `rememberSwetImage` goes through Coil, whose memory cache is keyed on the
                // model: the first row decodes the bitmap and every later row is a cache hit, and
                // concurrent requests for one URL are deduplicated rather than issued twice. What a
                // row costs is a painter and a state subscription, not a request. A group works the
                // same way with one entry per sender.
                if (!mine) {
                    SenderAvatar(
                        sender = message.sender,
                        announce = message.sender.displayName.takeIf { announceSender },
                    )
                }
            },
            bubble = {
                Column(
                    modifier = if (onBubblePositioned == null) Modifier else {
                        Modifier.onGloballyPositioned(onBubblePositioned)
                    },
                    horizontalAlignment = if (mine) Alignment.End else Alignment.Start,
                ) {
                    when (surface) {
                        MessageSurface.Photo -> PhotoMessage(
                            message = message,
                            mine = mine,
                            zone = zone,
                        )

                        MessageSurface.CaptionedPhoto -> CaptionedPhotoBubble(
                            message = message,
                            mine = mine,
                            zone = zone,
                            me = me,
                            members = members,
                        )

                        MessageSurface.Bubble -> TextBubble(
                            message = message,
                            mine = mine,
                            zone = zone,
                            me = me,
                            members = members,
                        )

                        MessageSurface.Voice -> VoiceBubble(
                            message = message,
                            mine = mine,
                            zone = zone,
                            me = me,
                            members = members,
                            playback = voicePlayback,
                            onToggle = onVoicePlaybackToggle,
                            onSeek = onVoicePlaybackSeek,
                            onSpeed = onVoicePlaybackSpeed,
                        )
                    }
                    MessageReactions(
                        reactions = message.reactions,
                        onReactionClick = onReactionClick,
                    )
                }
            },
        )

        if (failureNote != null) {
            Spacer(Modifier.height(Space.half))
            Text(
                text = failureNote,
                // §5.3 `caption` 12sp, and it deliberately did NOT follow the timestamp down to
                // 10sp: this is a SENTENCE the reader has to read, not a mark they glance at, and
                // it appears exactly when something has gone wrong.
                style = MaterialTheme.typography.labelSmall,
                color = colors.danger,
                // §22.2: the words carry the state, the colour only reinforces it.
                modifier = Modifier.padding(horizontal = Space.half),
            )
        }
    }
}

/**
 * Avatar and bubble on one line, bottom-aligned.
 *
 * TWO SLOTS, NOT THREE. The third was the timestamp, drawn on the canvas beside the bubble; it is
 * inside the bubble again, so the slot is gone rather than left present and always empty.
 *
 * A `Layout` RATHER THAN A `Row`, and the reason survives the slot that left. The bubble has to be
 * capped at a FRACTION of the row while still shrinking to its content, and in a `Row` those two
 * fight: `fillMaxWidth(fraction)` makes the bubble's slot the full fraction whatever is in it, so a
 * one-word own message would be pinned 255dp from the right-hand edge instead of against it, while
 * `wrapContentWidth` on top of it shrinks the drawn bubble but not the slot. Measuring the avatar
 * first and handing the bubble what is left, capped, is three lines of measure policy and gets both
 * ends right.
 *
 * BOTTOM ALIGNMENT IS THE ANSWER TO "THE AVATAR MUST SIT AT THE BOTTOM OF A BUBBLE WHOSE HEIGHT IT
 * DOES NOT KNOW". Nothing needs to know it: every child is placed at `height - its own height`, so a
 * one-line bubble and a forty-line one both end level with the avatar.
 *
 * The multi-content `Layout` overload takes a list of content lambdas and hands back a list of
 * measurable lists. It is NOT `SubcomposeLayout`: composition happens once, in the normal pass,
 * which matters because every one of these is inside a `LazyColumn`.
 */
@Composable
private fun MessageRow(
    mine: Boolean,
    leading: @Composable () -> Unit,
    bubble: @Composable () -> Unit,
) {
    val gap = with(LocalDensity.current) { MessageRowGap.roundToPx() }

    Layout(
        contents = listOf(leading, bubble),
        modifier = Modifier.fillMaxWidth(),
    ) { (leadingMeasurables, bubbleMeasurables), constraints ->
        // `fillMaxWidth` above is what makes this bounded. Guarded anyway: an unbounded width would
        // make the fraction meaningless and place everything at zero.
        val rowWidth = if (constraints.hasBoundedWidth) constraints.maxWidth else constraints.minWidth
        val loose = Constraints(maxWidth = rowWidth)

        // Measured FIRST, because what it takes is what the bubble may not have.
        val avatar = leadingMeasurables.firstOrNull()?.measure(loose)
        val leadingWidth = avatar?.let { it.width + gap } ?: 0

        val body = bubbleMeasurables.first().measure(
            Constraints(maxWidth = bubbleMaxWidthPx(rowWidth, leadingWidth)),
        )

        val height = maxOf(body.height, avatar?.height ?: 0)
        val used = leadingWidth + body.width
        // Incoming packs against the leading edge, an own message against the trailing one. The
        // order of the two is the same either way: avatar, then bubble.
        val start = if (mine) rowWidth - used else 0

        layout(rowWidth, height) {
            // DECLARED INSIDE THE PLACEMENT BLOCK, NOT OUTSIDE IT. A placement block can be run
            // again without a re-measure -- a layer change or a re-placement is enough -- and a
            // cursor captured from the measure scope would carry its previous value in, walking the
            // whole row off the right-hand edge on the second pass.
            //
            // `placeRelative`, NOT `place`. The two are identical under LTR and differ under RTL,
            // where `place` would keep drawing an own message hard against the LEFT edge with its
            // avatar slot on the left as well -- the layout mirrored for the reader's language and
            // this row alone refusing to. `start` is already written in terms of leading and
            // trailing rather than left and right, so the mirroring costs nothing but the word.
            // Only EN and RU ship today, so this is latent; it is one word now and a bug report
            // later.
            var x = start
            avatar?.let {
                it.placeRelative(x, height - it.height)
                x += it.width + gap
            }
            body.placeRelative(x, height - body.height)
        }
    }
}

/**
 * The sender's disc, at the bottom of the row.
 *
 * Presence is deliberately not drawn on it. The header already carries the green dot for a direct
 * chat, and a dot repeated down forty rows is decoration that changes under the reader while they
 * are reading.
 *
 * NO `surfaceColor` ARGUMENT, and it used to have one with a comment claiming it set the disc's
 * fallback background. It does not: inside `SwetAvatar` that parameter feeds exactly one thing, the
 * ring drawn behind the ONLINE DOT, and nothing here passes `isOnline`, so the argument had no
 * effect at all. The disc's own colour comes from `avatarBackground(displayName, colors)` either
 * way. (The same argument on the HEADER avatar is real and load-bearing, which is where it was
 * copied from.)
 *
 * [announce] IS THE SENDER'S NAME OR NULL, and it is the row's only attribution. `SwetAvatar` clears
 * the semantics of both its photo and its initials, so without this a screen reader in a GROUP got
 * strictly less than a sighted reader: message text with no sender, where the sighted reader has a
 * face on every row. In a 1:1 it stays null -- the header names the peer once, and repeating that
 * name down forty rows is the chatter §22.4 warns about.
 */
@Composable
private fun SenderAvatar(sender: User, announce: String?) {
    SwetAvatar(
        displayName = sender.displayName,
        avatarUrl = sender.avatarUrl,
        // 24dp: the mockup asks for 24-28 and this is the value inside that range which is already
        // on §11.5's ladder. A 26dp token would have been a fourth avatar size for one screen.
        size = Sizes.avatarExtraSmall,
        modifier = if (announce == null) {
            Modifier
        } else {
            // `clearAndSetSemantics`, not `semantics`: the avatar's children have already cleared
            // themselves, and this has to be the one node the reader hears for the disc.
            Modifier.clearAndSetSemantics { contentDescription = announce }
        },
    )
}

/** §7.1's radii, the SAME on all four corners. The mockup has no tail and no squared corner. */
private val BubbleShape: Shape = RoundedCornerShape(Radius.lg)

/**
 * The image inside a captioned bubble: its own smaller radius, inset from the bubble's edge.
 *
 * `Radius.sm` because the bubble is `Radius.lg` and the inset is `Space.one`: a nested rounded
 * rectangle looks wrong unless its radius is roughly the outer radius minus the inset, which is
 * 12dp here, and 10dp is the scale value next to it. A photo at the bubble's own 20dp would bulge.
 */
private val CaptionedPhotoShape: Shape = RoundedCornerShape(Radius.sm)

/**
 * The ordinary bubble: text or the deleted tombstone, with the time trailing the last word.
 *
 * TELEGRAM'S LAYOUT, WHICH IS ALSO SDS §12.4's ("timestamp appears inside bubble, bottom-right").
 * The time and the tick sit on the last LINE of the message when there is room for them, and drop
 * to a line of their own when there is not. [bubbleMetaLayout] is that decision and it is pure;
 * everything below is the plumbing that feeds it three measurements.
 *
 * WHERE THE LAST LINE'S WIDTH COMES FROM. Nothing in a measure pass can be asked "how wide is the
 * final line of this paragraph" -- the answer only exists after the text has been laid out, and
 * `onTextLayout` is the callback that carries it back. So the width is remembered, the first pass
 * runs with [UNKNOWN_LAST_LINE] and puts the metadata below, and the next pass has the number. It
 * SETTLES rather than oscillating, because the text is measured against the same cap in both
 * branches: the decision cannot change the measurement it was made from. Remembered per message
 * body, so an edit re-measures instead of laying the new text out against the old line.
 *
 * A `Layout` rather than a `Column` plus a `Row`, for the reason [MessageRow] is one: the bubble has
 * to be as wide as `max(text, last line + metadata)`, which is a number neither of those containers
 * can express -- a `Row` would push the whole paragraph aside to make room for the time.
 */
@Composable
private fun TextBubble(message: Message, mine: Boolean, zone: TimeZone, me: UserId, members: List<User>) {
    val chat = SwetgramThemeTokens.chat
    val onBubble = if (mine) chat.onOutgoingBubble else chat.onIncomingBubble
    val body = if (message.isDeleted) {
        // Its own sentence rather than `chat_preview_body_deleted`: this one stands where the
        // message used to be, and the preview's "Message deleted" reads as a label when it is the
        // whole bubble.
        stringResource(Res.string.chat_message_deleted_body)
    } else {
        message.text.orEmpty()
    }
    val gap = with(LocalDensity.current) { MetaGap.roundToPx() }

    Layout(
        contents = listOf(
            {
                Column {
                    if (message.replyTo != null) {
                        Quote(message.replyTo, me, members, onBubble)
                    }
                    if (message.picso != null && !message.isDeleted) {
                        PicsoShareCard(message = message, onBubble = onBubble)
                    } else {
                        Text(
                            text = body,
                            // 16sp, not §5.4's 17sp. The mockup puts message text at "around 15-16sp" and 16
                            // is the top of that range and already on the type scale. Recorded as part of
                            // the same override as the colours -- DECISIONS.md §25.
                            style = MaterialTheme.typography.bodyMedium,
                            // THE TOMBSTONE IS NOT SOFTENED EITHER, and that is a contrast fix rather than a
                            // style choice. It used to be drawn in the metadata colour to mark it as not
                            // really a message; `text.secondary` #A1A1AA on the near-white own bubble
                            // measures 2.3:1, which is a sentence the reader is meant to read and cannot.
                            // §22.2 has the words carrying the meaning anyway.
                            color = onBubble,
                        )
                    }
                }
            },
            {
                MetaRow(
                    message = message,
                    mine = mine,
                    zone = zone,
                    metaColor = bubbleMetaColor(onBubble),
                )
            },
        ),
        modifier = Modifier
            .clip(BubbleShape)
            .background(if (mine) chat.outgoingBubble else chat.incomingBubble)
            .padding(horizontal = Space.one + Space.half, vertical = Space.one),
    ) { (textMeasurables, metaMeasurables), constraints ->
        // The padding above has already come off this. Unbounded would make every comparison here
        // meaningless, so it degrades to the minimum rather than to `Int.MAX_VALUE` arithmetic.
        val maxWidth = if (constraints.hasBoundedWidth) constraints.maxWidth else constraints.minWidth
        val text = textMeasurables.first().measure(Constraints(maxWidth = maxWidth))
        val meta = metaMeasurables.first().measure(Constraints(maxWidth = maxWidth))

        val plan = bubbleMetaLayout(
            textWidth = text.width,
            textHeight = text.height,
            // Keep metadata on its own stable line. The previous onTextLayout feedback loop made a
            // new bubble change height one frame after it appeared and could move the timeline.
            lastLineWidth = UNKNOWN_LAST_LINE,
            metaWidth = meta.width,
            metaHeight = meta.height,
            gap = gap,
            maxWidth = maxWidth,
        )

        layout(plan.width, plan.height) {
            text.placeRelative(0, 0)
            // Trailing edge in both branches, and never negative: a bubble too narrow to hold the
            // metadata at all lets it overflow to the trailing side rather than off the leading one,
            // where it would cross the message text.
            val metaX = (plan.width - meta.width).coerceAtLeast(0)
            // Beside: bottom-aligned with the text, so the time sits on the last line's baseline
            // rather than floating at the top of a paragraph. Below: directly under it.
            val metaY = if (plan.metaBesideText) plan.height - meta.height else text.height
            meta.placeRelative(metaX, metaY)
        }
    }
}

@Composable
private fun VoiceBubble(
    message: Message,
    mine: Boolean,
    zone: TimeZone,
    me: UserId,
    members: List<User>,
    playback: VoicePlaybackState,
    onToggle: (Message) -> Unit,
    onSeek: (Message, Long) -> Unit,
    onSpeed: (Message) -> Unit,
) {
    val chat = SwetgramThemeTokens.chat
    val colors = SwetgramThemeTokens.colors
    val onBubble = if (mine) chat.onOutgoingBubble else chat.onIncomingBubble
    val active = playback.messageId == message.clientId
    val duration = (if (active) playback.durationMs else message.voiceDurationMs) ?: 0L
    val position = if (active) playback.positionMs else 0L
    val playing = active && playback.isPlaying
    val preparing = active && playback.isPreparing
    val progress = if (duration > 0) (position.toFloat() / duration).coerceIn(0f, 1f) else 0f
    // Old messages created before waveform metadata use an honest, quiet placeholder. Do not
    // fabricate a lively pattern that looks as though it was measured from their audio.
    val waveform = message.voiceWaveform.ifEmpty { List(32) { 12 } }

    Column(
        modifier = Modifier
            .clip(BubbleShape)
            .background(if (mine) chat.outgoingBubble else chat.incomingBubble)
            .padding(horizontal = Space.one + Space.half, vertical = Space.one),
    ) {
        if (message.replyTo != null) {
            Quote(message.replyTo, me, members, onBubble)
            Spacer(Modifier.height(Space.half))
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            SwetIconButton(
                contentDescription = stringResource(
                    when {
                        preparing -> Res.string.chat_voice_preparing
                        playing -> Res.string.chat_voice_playback_pause
                        else -> Res.string.chat_voice_play
                    },
                ),
                onClick = { onToggle(message) },
                enabled = !preparing,
            ) { tint ->
                if (preparing) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(Sizes.iconSmall),
                        color = tint,
                        strokeWidth = 2.dp,
                    )
                } else {
                    SwetIcon(
                        icon = if (playing) SwetIcons.Pause else SwetIcons.Play,
                        tint = tint,
                        contentDescription = null,
                        size = Sizes.iconSmall,
                    )
                }
            }
            VoiceWaveform(
                samples = waveform,
                progress = progress,
                playedColor = colors.accent,
                remainingColor = bubbleMetaColor(onBubble),
                modifier = Modifier
                    .weight(1f)
                    .height(36.dp)
                    .pointerInput(message.clientId, duration) {
                        detectTapGestures { point ->
                            if (!preparing && duration > 0 && size.width > 0) {
                                onSeek(message, (point.x / size.width * duration).toLong())
                            }
                        }
                    },
            )
            Spacer(Modifier.width(Space.one))
            Text(
                text = formatVoiceDuration(if (active && position > 0) position else duration),
                style = SwetgramCaptionSmall,
                color = bubbleMetaColor(onBubble),
            )
            Spacer(Modifier.width(Space.one))
            val speed = if (active) playback.speed else 1f
            val speedText = "${speed.toString().removeSuffix(".0")}×"
            val speedLabel = stringResource(Res.string.chat_voice_speed, speedText)
            Text(
                text = speedText,
                style = MaterialTheme.typography.labelSmall,
                color = onBubble,
                modifier = Modifier
                    .clip(RoundedCornerShape(Radius.full))
                    .clickable(enabled = !preparing) { onSpeed(message) }
                    .padding(horizontal = Space.one, vertical = Space.half)
                    .clearAndSetSemantics {
                        contentDescription = speedLabel
                    },
            )
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(
                text = stringResource(Res.string.chat_voice_message),
                style = SwetgramCaptionSmall,
                color = bubbleMetaColor(onBubble),
            )
            MetaRow(message, mine, zone, bubbleMetaColor(onBubble))
        }
    }
}

@Composable
private fun VoiceWaveform(
    samples: List<Int>,
    progress: Float,
    playedColor: Color,
    remainingColor: Color,
    modifier: Modifier = Modifier,
) {
    Canvas(modifier) {
        val count = samples.size.coerceAtLeast(1)
        val step = size.width / count
        samples.forEachIndexed { index, raw ->
            val level = (raw.coerceIn(0, 100) / 100f).coerceAtLeast(0.12f)
            val x = step * index + step / 2
            val half = size.height * level / 2
            drawLine(
                color = if ((index + 1f) / count <= progress) playedColor else remainingColor,
                start = Offset(x, size.height / 2 - half),
                end = Offset(x, size.height / 2 + half),
                strokeWidth = (step * 0.45f).coerceAtLeast(1f),
                cap = StrokeCap.Round,
            )
        }
    }
}

/**
 * A photo sent with no caption: no bubble at all.
 *
 * The image is the surface. The metadata is laid OVER its bottom-right on a scrim -- a pill rather
 * than the bottom-edge gradient WhatsApp uses, because a gradient has to be tuned against an
 * assumption about the photo and the assumption fails on the exact images that matter: a snow field,
 * a screenshot of a white page. The pill carries its own contrast wherever it lands, and the
 * arithmetic behind its alpha is in [SwetOnImage.scrim].
 *
 * Only [mine] varies by direction, and only because the delivery tick is drawn on own messages
 * alone. Nothing about the SURFACE differs; the row does the placing.
 */
@Composable
private fun PhotoMessage(message: Message, mine: Boolean, zone: TimeZone) {
    // Wraps the photo exactly, so the overlay's BottomEnd is the PHOTO's corner rather than the
    // corner of whatever slack the row happens to have.
    Box(modifier = Modifier.fillMaxWidth()) {
        ChatPhoto(url = message.imageUrl, shape = BubbleShape)
        PhotoMeta(
            message = message,
            mine = mine,
            zone = zone,
            modifier = Modifier.align(Alignment.BottomEnd),
        )
    }
}

/**
 * A photo sent WITH a caption: the bubble stays, and the caption is ABOVE the picture.
 *
 * ABOVE, WHICH IS A CHANGE. This used to be Telegram's layout -- photo flush to the bubble's top
 * edge, caption underneath -- and the mockup is explicit that it is the other way round: the caption
 * sits above an image that is INSET inside the bubble with its own smaller radius, and the timestamp
 * is on the image rather than under it.
 *
 * The inset is what makes the reading order work. With the photo flush to the edge the bubble had no
 * top, so the caption read as a caption; inset on all four sides, the picture reads as an attachment
 * to the sentence above it, which is what a caption above a photo is.
 */
@Composable
private fun CaptionedPhotoBubble(message: Message, mine: Boolean, zone: TimeZone, me: UserId, members: List<User>) {
    val chat = SwetgramThemeTokens.chat
    val onBubble = if (mine) chat.onOutgoingBubble else chat.onIncomingBubble
    Column(
        modifier = Modifier
            .clip(BubbleShape)
            .background(if (mine) chat.outgoingBubble else chat.incomingBubble)
            .padding(Space.one),
    ) {
        if (message.replyTo != null) {
            Quote(message.replyTo, me, members, onBubble)
            Spacer(Modifier.height(Space.half))
        }

        Text(
            text = message.text.orEmpty(),
            // The same size as any other message text: a caption is a message that happens to have
            // a picture attached, not a smaller class of writing.
            style = MaterialTheme.typography.bodyMedium,
            color = if (mine) chat.onOutgoingBubble else chat.onIncomingBubble,
            modifier = Modifier.padding(horizontal = Space.half, vertical = Space.half),
        )

        Box {
            ChatPhoto(url = message.imageUrl, shape = CaptionedPhotoShape)
            PhotoMeta(
            message = message,
            mine = mine,
            zone = zone,
            modifier = Modifier.align(Alignment.BottomEnd),
        )
        }
    }
}

/**
 * The timestamp and tick laid over a photo, on their scrim.
 *
 * One definition for both photo surfaces. The mark is white, and nothing is lost by that: §22
 * requires the state to be carried by the SHAPE -- one check versus two -- and a tint that has to
 * survive an arbitrary photograph cannot also be a signal.
 *
 * IT FOLLOWED THE BUBBLE'S METADATA DOWN TO 10sp, because there is one metadata size on this screen
 * and a photo's timestamp reading larger than the one two rows above it would look like a different
 * kind of fact. The scrim still carries it: [SwetOnImage.scrim]'s alpha was derived so that white on
 * it measures 4.74:1 against the worst case a photograph can be, and a contrast ratio does not vary
 * with size -- 4.74:1 clears AA for normal text at 10sp exactly as it did at 12sp.
 */
@Composable
private fun PhotoMeta(
    message: Message,
    mine: Boolean,
    zone: TimeZone,
    modifier: Modifier = Modifier,
) {
    MetaRow(
        message = message,
        mine = mine,
        zone = zone,
        modifier = modifier
            // Inset from the photo's edge, then the pill's own padding inside that.
            .padding(Space.one)
            .clip(RoundedCornerShape(Radius.full))
            .background(SwetOnImage.scrim)
            .padding(horizontal = Space.one, vertical = Space.half),
        metaColor = SwetOnImage.content,
    )
}

@Composable
private fun PicsoShareCard(message: Message, onBubble: Color) {
    val colors = SwetgramThemeTokens.colors
    Column(Modifier.padding(bottom = Space.half), verticalArrangement = Arrangement.spacedBy(Space.half)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(Space.half)) {
            SwetIcon(SwetIcons.ImageIcon, tint = colors.accent, contentDescription = null, size = 18.dp)
            Text(stringResource(swetgram.shared.generated.resources.Res.string.picso_shared_message),
                style = MaterialTheme.typography.labelLarge, color = onBubble)
        }
        Text(stringResource(swetgram.shared.generated.resources.Res.string.picso_open_shared),
            style = MaterialTheme.typography.bodySmall, color = onBubble.copy(alpha = 0.7f))
    }
}

/**
 * The photo itself, in whatever [shape] its surface asked for.
 *
 * SIZED BY ASPECT RATIO, never in dp: the block then fills its surface's own maximum width on any
 * screen instead of the two disagreeing on a narrow phone. The ratio is the IMAGE's own once it has
 * decoded -- a portrait photo stays portrait -- but clamped by [photoBoxAspectRatio], because a
 * 9:16 phone photo drawn at its true proportions is taller than the screen it is being read on and
 * pushes every other message out of view. Past the clamp the image is cropped rather than squashed,
 * which is what [ContentScale.Crop] is for. Within the clamp Crop and Fit are the same operation,
 * so nothing is lost on an image that fits.
 *
 * [url] stays nullable even though [messageSurface] has already rejected a blank one for the two
 * photo surfaces: this is the last thing between a null and `rememberSwetImage`, and a failure
 * drawn as words costs one branch.
 */
@Composable
private fun ChatPhoto(url: String?, shape: Shape) {
    val colors = SwetgramThemeTokens.colors

    if (url.isNullOrBlank()) {
        ImageUnavailable(shape)
        return
    }

    val image = rememberSwetImage(url)
    val ratio = photoBoxAspectRatio(image.aspectRatio)

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .aspectRatio(ratio)
            .clip(shape),
        contentAlignment = Alignment.Center,
    ) {
        when (image.phase) {
            // Not `SwetSkeleton`, which is hardcoded to `colors.card` -- and an incoming bubble IS
            // the card colour, so that skeleton would be an invisible block on exactly half the
            // messages. The pulse modifier is the part worth sharing; the colour is this context's.
            SwetImagePhase.Loading -> Box(
                Modifier
                    .fillMaxSize()
                    .swetSkeletonPulse()
                    .background(colors.surface),
            )

            SwetImagePhase.Failed -> ImageUnavailable(shape)

            SwetImagePhase.Loaded -> Image(
                painter = image.painter,
                contentDescription = stringResource(Res.string.chat_photo),
                contentScale = ContentScale.Crop,
                modifier = Modifier
                    .fillMaxSize()
                    // `surface` underneath: a PNG with transparency would otherwise composite
                    // straight onto whatever is behind it -- the black canvas for a bare photo, the
                    // bubble for a captioned one.
                    .background(colors.surface)
                    // §19.2's `motion.fast`, and the reason Coil's own crossfade is switched off
                    // in `swetImageLoader`: this one collapses under `LocalReduceMotion`.
                    .swetAppear(),
            )
        }
    }
}

/**
 * What is drawn where a photo cannot be.
 *
 * Words, not a blank box. §18.2 asks for what happened rather than the raw error, and a grey
 * rectangle is indistinguishable from a photo of a grey wall -- the reader cannot tell whether the
 * app failed or the sender did something odd. Deliberately NOT offering a retry: the loader retries
 * on its own the next time this scrolls back into view, and a button that only sometimes works is
 * worse than none.
 *
 * Takes the same [shape] as the photo it stands in for, so a failure occupies the same silhouette
 * as a success.
 */
@Composable
private fun ImageUnavailable(shape: Shape) {
    val colors = SwetgramThemeTokens.colors
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .aspectRatio(PHOTO_ASPECT_RATIO)
            .clip(shape)
            .background(colors.surface),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = stringResource(Res.string.chat_photo_unavailable),
            style = MaterialTheme.typography.labelMedium,
            // `textSecondary`, never `textTertiary`: that token is reserved for placeholder and
            // disabled text, and this is neither -- it is the only thing the reader is told.
            color = colors.textSecondary,
        )
    }
}

/**
 * The proportions the photo's box is laid out at, given what the decoder has reported.
 *
 * Extracted as pure logic for the reason `SwetMotion.kt` extracts its own decisions: it is the part
 * of this that can be wrong in a way nobody notices until a particular photo arrives, and this host
 * cannot run a composable to find out (CLAUDE.md: no emulator, no device in the build).
 *
 * A null [imageRatio] -- still loading, failed, or a painter that will not say -- falls back to
 * [PHOTO_ASPECT_RATIO] so the box has a stable size to hold before the bytes land. A NaN would
 * survive `coerceIn` and then throw inside `Modifier.aspectRatio`, so it is treated as unknown.
 */
internal fun photoBoxAspectRatio(imageRatio: Float?): Float {
    val ratio = imageRatio?.takeIf { it.isFinite() && it > 0f } ?: PHOTO_ASPECT_RATIO
    return ratio.coerceIn(MIN_PHOTO_ASPECT_RATIO, MAX_PHOTO_ASPECT_RATIO)
}

@Composable
private fun Quote(
    replyTo: MessagePreview,
    me: UserId,
    members: List<User>,
    onBubble: Color,
) {
    val sender = members.firstOrNull { it.id == replyTo.senderId }
    val senderName = when {
        replyTo.senderId == me -> stringResource(Res.string.group_member_you)
        !replyTo.senderDisplayName.isNullOrBlank() -> replyTo.senderDisplayName
        sender != null -> sender.displayName
        else -> "User"
    }

    Row(
        modifier = Modifier
            .padding(bottom = Space.half)
            .fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Vertical accent bar
        Box(
            Modifier
                .width(Sizes.borderHairline * 2)
                .height(32.dp)
                .background(onBubble.copy(alpha = 0.5f))
        )
        Spacer(Modifier.width(Space.one))
        Column {
            Text(
                text = senderName,
                style = MaterialTheme.typography.labelMedium,
                color = onBubble,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = replyTo.text ?: stringResource(
                    if (replyTo.kind == MessageKind.VOICE) {
                        Res.string.chat_voice_message
                    } else {
                        Res.string.chat_preview_body_photo
                    },
                ),
                style = MaterialTheme.typography.bodySmall.copy(fontSize = 12.sp),
                color = onBubble.copy(alpha = 0.7f),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

/** The 4:3 of a phone photo. Used only before the real proportions are known, and on failure. */
private const val PHOTO_ASPECT_RATIO = 4f / 3f

/**
 * The tallest a photo may be drawn: 3:4, one step past portrait.
 *
 * At the bubble's 72% of a 360dp screen that is a 236x315dp block -- large enough to see, and short
 * enough that the message above it and the composer below it are both still on screen.
 */
private const val MIN_PHOTO_ASPECT_RATIO = 3f / 4f

/**
 * The widest: 2:1. Beyond that a panorama drawn to the bubble's width is a letterbox strip a few
 * dozen dp tall, which is not a picture anybody can look at.
 */
private const val MAX_PHOTO_ASPECT_RATIO = 2f

/**
 * Time, "edited", and the delivery tick.
 *
 * Ticks appear only on your OWN messages -- that is where they carry meaning. DECISIONS §5
 * fixes the states as sending / sent / read / failed, with no delivered state in the MVP.
 *
 * ONE COLOUR, WHERE THERE USED TO BE TWO. The second was `readTickColor`, so that the read state
 * could be tinted apart from the timestamp; every call site passed it the same value the timestamp
 * had -- the mockup draws both grey, and a photo draws both white -- and a parameter with one
 * possible argument is a distinction the screen does not make. Re-tinting the read state is now one
 * line in [DeliveryTick], next to the `danger` the failed state already reaches for by itself.
 *
 * The colour is still a PARAMETER rather than read from the theme here, because this row is drawn on
 * two kinds of backdrop: a bubble, where it is that bubble's own foreground softened
 * ([bubbleMetaColor]), and a scrim over an arbitrary photograph, where it is white.
 *
 * EVERYTHING IN IT IS 10sp -- see `SwetgramCaptionSmall`. `caption` 12sp was the old size and the
 * user asked for smaller after seeing it on a phone; the whole row moved together, because "edited"
 * set one point larger than the time beside it would read as a word rather than as a note.
 */
@Composable
private fun MetaRow(
    message: Message,
    mine: Boolean,
    zone: TimeZone,
    metaColor: Color,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (message.isEdited && !message.isDeleted) {
            Text(
                text = stringResource(Res.string.chat_edited),
                style = SwetgramCaptionSmall,
                color = metaColor,
                maxLines = 1,
                overflow = TextOverflow.Clip,
            )
            Spacer(Modifier.width(Space.half))
        }
        Text(
            // 24-hour, `18:45`.
            text = formatClockTime(message.sentAt, zone),
            style = SwetgramCaptionSmall,
            color = metaColor,
            maxLines = 1,
        )
        if (mine) {
            Spacer(Modifier.width(Space.half))
            DeliveryTick(message.state, metaColor)
        }
    }
}

/**
 * FOUR MARKS FROM THE ICON SET, WHERE THERE WERE FOUR CANVAS DRAWINGS.
 *
 *   SENDING  [SwetIcons.Clock]
 *   SENT     [SwetIcons.Check]
 *   READ     [SwetIcons.CheckDouble]
 *   FAILED   [SwetIcons.AlertCircle], in the danger colour
 *
 * The paragraph that used to stand here argued that Compose Multiplatform ships no icon set at this
 * version and that inventing path data was a poor trade. `ui/design/SwetIcons.kt` IS that set --
 * one 24 grid, one 2.0 stroke, proofed against a rasterised sheet -- so the argument is spent, and
 * with it the eyeballed fractions that made this tick's stroke a ratio of its box rather than the
 * one weight every other mark on the screen is drawn at.
 *
 * THE READ RECEIPT IS ONE ICON NOW RATHER THAN THE SENT CHECK DRAWN TWICE, and that is a LAYOUT fix
 * as much as a drawing one. The two draws sat at two offsets, so a read pair was wider than a single
 * check by the distance between them -- and this tick lives inside the bubble beside the timestamp
 * (see [MetaRow] and [bubbleMetaLayout]), so a message being READ silently widened its own bubble
 * under the reader's eye. One vector at one size cannot do that.
 *
 * The state is carried by the SHAPE, never by colour alone -- one check versus two is legible to a
 * reader who cannot tell two tints apart (SDS §22), and it has to be, because `MessageState` has no
 * DELIVERED rung: the COUNT is the entire read-receipt model. That is why the box is
 * [Sizes.iconMetaTick] 12dp rather than the 8dp a literal halving of the old 16dp would have given
 * -- see that token for the arithmetic of what smears at 8dp and what does not at 12dp.
 *
 * ONE COLOUR PARAMETER. The read state used to take its own so that it could be tinted apart from
 * the timestamp; the mockup draws both grey and every call site passed the same value. Tinting it
 * again is one line HERE, which is where [MessageState.FAILED] already reaches for `danger` on its
 * own -- so the file already proves a state can name its own colour without a parameter for it.
 *
 * NO CONTENT DESCRIPTION, which is what the `Canvas` had: the mark is silent to a screen reader and
 * always was. Naming it is a copy decision with four sentences in it and is not this pass's.
 */
@Composable
private fun DeliveryTick(state: MessageState, metaColor: Color) {
    val errorColor = SwetgramThemeTokens.colors.danger
    SwetIcon(
        icon = when (state) {
            MessageState.SENDING -> SwetIcons.Clock
            MessageState.SENT -> SwetIcons.Check
            MessageState.READ -> SwetIcons.CheckDouble
            MessageState.FAILED -> SwetIcons.AlertCircle
        },
        tint = if (state == MessageState.FAILED) errorColor else metaColor,
        contentDescription = null,
        size = Sizes.iconMetaTick,
    )
}

/*
 * THE CHECK'S PROPORTIONS, AS FRACTIONS OF ITS BOX, AND WHY THEY ARE NAMED.
 *
 * They were four literals inside the draw scope while the box was 16dp and nobody had to look at
 * them. The box is 12dp now (see [Sizes.iconMetaTick]), and at that size the read state is the
 * smallest thing on the screen that MEANS anything -- one check against two is the whole delivery
 * model, because `MessageState` has no DELIVERED rung. So the two properties that keep it readable
 * are pinned by `MessageLayoutTest` instead of being trusted: the pair stays inside its own box, and
 * the two checks stay far enough apart to be two.
 *
 * A canvas does not clip, so the OLD offsets were a latent defect this move surfaced rather than
 * caused: -0.18 put the first check of a read pair at -0.08 of the box, drawing it 1.28dp outside
 * its own bounds and over whatever the layout had put beside it -- which, now that the tick sits
 * inside a bubble beside a timestamp, is the timestamp. The pair is shifted to start at the box edge
 * and the separation between the two checks is unchanged, so nothing about the look moves except the
 * 8% that was never inside the glyph.
 */

/** The near end of a check's short stroke, and [TICK_CHECK_END] the far end of its long one. */
internal const val TICK_CHECK_START = 0.10f
internal const val TICK_CHECK_END = 0.72f

/** §9.3's one stroke width, as a fraction of the box. 1.08dp at 12dp. */
internal const val TICK_STROKE_FRACTION = 0.09f

/** The leading check of a READ pair: shifted so the pair begins exactly at the box's edge. */
internal const val READ_TICK_FIRST_OFFSET = -TICK_CHECK_START

/** The trailing check, overlapping the first as in every messenger. */
internal const val READ_TICK_SECOND_OFFSET = 0.22f
